import React, { useEffect, useLayoutEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { CheckCircle, ExternalLink, FlaskConical } from 'lucide-react';
import { contentApi } from '../../services/contentApi';
import { CourseDetail, CourseEnrollment, CourseInterviewPackageConfig, CourseListItem, CourseProgress, KnowledgeTopic } from '../../types/content';
import Button from '../../components/UI/Button';
import ContentBlocks from '../../components/content/ContentBlocks';
import CourseLearnAside from '../../components/content/CourseLearnAside';
import AuthorCard from '../../components/content/AuthorCard';
import CourseRating, { readCourseRating } from '../../components/content/CourseRating';
import CourseToc from '../../components/content/CourseToc';
import MobileHamburgerNav from '../../components/content/MobileHamburgerNav';
import TopicNavLinks from '../../components/content/TopicNavLinks';
import { PageLoading } from '../../components/UI/LoadingOverlay';
import { onLabCompletedMessage } from '../../utils/labCompletion';
import { courseTopicPath } from '../../utils/coursePaths';

const CourseTopicPage: React.FC = () => {
  const { slug, topicSlug } = useParams();
  const [searchParams] = useSearchParams();
  const moduleParam = Number(searchParams.get('module') || 0) || undefined;
  const navigate = useNavigate();
  const [topic, setTopic] = useState<KnowledgeTopic | null>(null);
  const [course, setCourse] = useState<CourseListItem | null>(null);
  const [moduleTitle, setModuleTitle] = useState<string | null>(null);
  const [topicModuleId, setTopicModuleId] = useState<number | null>(null);
  const [moduleAuthor, setModuleAuthor] = useState<CourseListItem['author'] | null>(null);
  const [assessmentId, setAssessmentId] = useState<number | null>(null);
  const [nextAssessmentId, setNextAssessmentId] = useState<number | null>(null);
  const [previousAssessmentId, setPreviousAssessmentId] = useState<number | null>(null);
  const [nextSlug, setNextSlug] = useState<string | null>(null);
  const [nextTitle, setNextTitle] = useState<string | null>(null);
  const [nextModuleTitle, setNextModuleTitle] = useState<string | null>(null);
  const [previousSlug, setPreviousSlug] = useState<string | null>(null);
  const [previousTitle, setPreviousTitle] = useState<string | null>(null);
  const [previousModuleTitle, setPreviousModuleTitle] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState<CourseProgress | null>(null);
  const [outline, setOutline] = useState<CourseDetail | null>(null);
  const [completing, setCompleting] = useState(false);
  const [interviewPackageCount, setInterviewPackageCount] = useState(0);
  const [enrollment, setEnrollment] = useState<CourseEnrollment | null>(null);
  const [interviewPackages, setInterviewPackages] = useState<CourseInterviewPackageConfig | null>(null);
  const [contentLoading, setContentLoading] = useState(false);
  const [shellReady, setShellReady] = useState(false);
  const [mobileContentsOpen, setMobileContentsOpen] = useState(false);

  useLayoutEffect(() => {
    document.getElementById('course-topic-content')?.focus({ preventScroll: true });
  }, [topicSlug]);

  useEffect(() => {
    setMobileContentsOpen(false);
  }, [topicSlug]);

  const loadProgress = (courseId: number) => {
    contentApi.getCourseProgress(courseId).then((r) => setProgress(r.data.progress || null)).catch(() => undefined);
  };

  const loadInterviewPackage = (courseId: number) => {
    contentApi.getCourseEnrollment(courseId)
      .then((r) => {
        const next = r.data.enrollment || null;
        setEnrollment(next);
        setInterviewPackageCount(Number(next?.interviewPackageCount || 0));
      })
      .catch(() => {
        setEnrollment(null);
        setInterviewPackageCount(0);
      });
  };

  useEffect(() => {
    contentApi.getCourseInterviewPackages()
      .then((r) => setInterviewPackages(r.data))
      .catch(() => setInterviewPackages(null));
  }, []);

  useEffect(() => {
    if (!course?.id) return undefined;
    loadInterviewPackage(course.id);
    return onLabCompletedMessage((_, courseId) => {
      if (!courseId || courseId === course.id) loadProgress(course.id);
    });
  }, [course?.id]);

  useEffect(() => {
    if (!slug) return;
    contentApi.getCourseBySlug(slug).then((r) => {
      setOutline(r.data.course || null);
      if (r.data.course) {
        setCourse((current) => current || {
          id: r.data.course!.id,
          title: r.data.course!.title,
          slug: r.data.course!.slug,
          categoryName: r.data.course!.categoryName,
          averageRating: r.data.course!.averageRating,
          ratingCount: r.data.course!.ratingCount,
          author: r.data.course!.author,
        } as CourseListItem);
      }
    }).catch(() => undefined);
  }, [slug]);

  useEffect(() => {
    if (!slug || !topicSlug) return;
    let cancelled = false;
    setContentLoading(true);
    setError(null);
    contentApi.getCourseTopic(slug, topicSlug, moduleParam).then((r) => {
      if (cancelled) return;
      if (r.data.canLearn === false) {
        if (r.data.access === 'PaymentRequired') {
          navigate(`/courses/${slug}/payment`, { replace: true });
        } else {
          navigate(`/courses/${slug}`, { replace: true });
        }
        return;
      }
      setTopic(r.data.topic);
      if (r.data.course) setCourse(r.data.course);
      setModuleTitle(r.data.moduleTitle || null);
      setTopicModuleId(r.data.moduleId || moduleParam || null);
      setModuleAuthor(r.data.moduleAuthor || null);
      setAssessmentId(r.data.assessmentId || null);
      setNextAssessmentId(r.data.nextAssessmentId || null);
      setPreviousAssessmentId(r.data.previousAssessmentId || null);
      setNextSlug(r.data.nextTopicSlug || null);
      setNextTitle(r.data.nextTopicTitle || null);
      setNextModuleTitle(r.data.nextModuleTitle || null);
      setPreviousSlug(r.data.previousTopicSlug || null);
      setPreviousTitle(r.data.previousTopicTitle || null);
      setPreviousModuleTitle(r.data.previousModuleTitle || null);
      setShellReady(true);
      if (r.data.course?.id && r.data.topic?.id && r.data.canLearn !== false) {
        loadInterviewPackage(r.data.course.id);
        if (r.data.nextTopicSlug) {
          contentApi.completeTopic(r.data.course.id, r.data.topic.id)
            .then((p) => setProgress(p.data.progress || null))
            .catch(() => loadProgress(r.data.course!.id));
        } else {
          loadProgress(r.data.course.id);
        }
      }
    }).catch(() => {
      if (!cancelled) setError('This topic is not available.');
    }).finally(() => {
      if (!cancelled) setContentLoading(false);
    });
    return () => { cancelled = true; };
  }, [slug, topicSlug, moduleParam, navigate]);

  if (error && !shellReady) return <div className="max-w-3xl mx-auto px-4 py-16 text-gray-600">{error}</div>;
  if (!shellReady || !course) return <PageLoading label="Loading course…" />;

  const topicDone = !!(topic?.id && progress?.completedTopicIds?.includes(topic.id));
  const isLastTopic = !nextSlug;
  const currentProgressModule = progress?.modules.find((module) =>
    module.topics.some((item) => item.slug === topic?.slug || item.id === topic?.id)
  );
  const moduleDone = !!currentProgressModule?.isCompleted;
  const assessmentPending = !!(assessmentId && currentProgressModule?.assessmentRequired && !currentProgressModule.assessmentPassed);
  const objectives = (topic?.objective || '').split(/\r?\n/).map((l) => l.trim()).filter(Boolean);

  const markTopicDone = () => {
    if (!course?.id || !topic?.id || topicDone || completing) return;
    setCompleting(true);
    contentApi.completeTopic(course.id, topic.id)
      .then((p) => setProgress(p.data.progress || null))
      .catch(() => undefined)
      .finally(() => setCompleting(false));
  };

  const tocModules = (progress?.modules?.length
    ? progress.modules
    : outline?.modules || []).map((module) => ({
    id: module.id,
    title: module.title,
    isCompleted: 'isCompleted' in module
      ? module.isCompleted
      : module.topics.every((item) => !!(item.id && progress?.completedTopicIds?.includes(item.id))),
    topics: module.topics.map((item) => {
      const fromProgress = 'labs' in item ? item.labs : undefined;
      const fromOutline = 'relatedLabs' in item ? item.relatedLabs : undefined;
      const labs = (fromProgress || fromOutline || []).map((lab) => ({
        id: lab.id,
        name: lab.name,
        slug: lab.slug,
        isCompleted: 'isCompleted' in lab ? !!lab.isCompleted : !!(progress?.completedLabIds?.includes(lab.id)),
      }));
      return {
        title: item.title,
        slug: item.slug,
        displayOrder: item.displayOrder,
        isCompleted: 'isCompleted' in item ? item.isCompleted : !!(item.id && progress?.completedTopicIds?.includes(item.id)),
        labs,
      };
    }),
    assessments: ('assessments' in module ? module.assessments : []).map((assessment) => ({
      id: assessment.id,
      title: assessment.title,
      displayOrder: assessment.displayOrder,
      passed: 'passed' in assessment
        ? assessment.passed
        : !!(assessment.id && progress?.passedAssessmentIds?.includes(assessment.id)),
    })),
  }));

  return (
    <article className="outline-none">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 grid lg:grid-cols-[17rem_minmax(0,1fr)_17rem] gap-8 items-start">
        {tocModules.length > 0 && (
          <div className="lg:hidden order-1">
            <MobileHamburgerNav
              open={mobileContentsOpen}
              onToggle={() => setMobileContentsOpen((open) => !open)}
              label="table of contents"
            >
              <CourseToc
                courseSlug={course.slug}
                modules={tocModules}
                currentTopicSlug={topic?.slug}
                currentModuleId={topicModuleId}
                interviewPackageCount={interviewPackageCount}
                courseId={course.id}
                courseTitle={course.title}
                showInterviewLinks={false}
                onNavigate={() => setMobileContentsOpen(false)}
              />
            </MobileHamburgerNav>
          </div>
        )}
        {tocModules.length > 0 && (
          <aside className="hidden lg:block lg:sticky lg:top-24 lg:max-h-[calc(100vh-7rem)] lg:overflow-y-auto order-1">
            <CourseToc
              courseSlug={course.slug}
              modules={tocModules}
              currentTopicSlug={topic?.slug}
              currentModuleId={topicModuleId}
              interviewPackageCount={interviewPackageCount}
              courseId={course.id}
              courseTitle={course.title}
              showInterviewLinks={false}
            />
          </aside>
        )}

        <div id="course-topic-content" tabIndex={-1} className="order-2 outline-none min-w-0 relative">
          {contentLoading && (
            <div className="absolute inset-0 z-10 bg-white/70 flex items-start justify-center pt-16 text-sm text-gray-600">
              Loading topic…
            </div>
          )}
          {error && <p className="text-error-600 mb-4">{error}</p>}
          {topic && (
            <>
              <nav className="text-sm text-gray-500 flex flex-wrap items-center gap-x-2 gap-y-1">
                <Link to="/courses" className="text-primary-600 font-medium hover:text-primary-800">Courses</Link>
                {course.categoryName && (
                  <>
                    <span>/</span>
                    <span>{course.categoryName}</span>
                  </>
                )}
                <span>/</span>
                <Link to={`/courses/${course.slug}`} className="text-primary-600 font-medium hover:text-primary-800">{course.title}</Link>
                {moduleTitle && (
                  <>
                    <span>/</span>
                    <span>{moduleTitle}</span>
                  </>
                )}
                <span>/</span>
                <span className="text-gray-800">{topic.title}</span>
              </nav>
              {moduleTitle && <p className="mt-4 text-xs uppercase tracking-wide text-gray-500">{moduleTitle}</p>}
              <h1 className="mt-2 text-3xl md:text-4xl font-bold text-gray-900 inline-flex items-center gap-2">
                {topic.title}
                {(topicDone || progress?.isCourseCompleted) ? <CheckCircle className="h-7 w-7 text-success-600" aria-label="Completed" /> : null}
              </h1>
              <p className="mt-2">
                <CourseRating
                  average={readCourseRating(outline || course).average}
                  count={readCourseRating(outline || course).count}
                  className="text-sm text-amber-700"
                  showEmpty
                />
                <span className="ml-2 text-sm text-gray-500">{topic.estimatedMinutes} min</span>
              </p>
              {moduleAuthor?.displayName ? (
                <div className="mt-6 max-w-xl"><AuthorCard author={moduleAuthor} compact /></div>
              ) : null}

              {objectives.length > 0 && (
                <section className="mt-8">
                  <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">What you&apos;ll learn</h2>
                  <ul className="mt-3 space-y-1 text-gray-700">
                    {objectives.map((line) => <li key={line}>• {line}</li>)}
                  </ul>
                </section>
              )}

              <section className="mt-10">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Lesson</h2>
                <ContentBlocks blocks={topic.blocks || []} />
              </section>

              {(topic.relatedLabs || []).length > 0 && (
                <section className="mt-10 rounded-2xl border border-primary-200 bg-primary-50 p-5">
                  <h2 className="font-semibold text-primary-900">Hands-on labs for this topic</h2>
                  <ul className="mt-3 space-y-3">
                    {topic.relatedLabs.map((lab) => {
                      const done = !!progress?.completedLabIds?.includes(lab.id);
                      return (
                        <li key={lab.id} className="flex items-center justify-between gap-3">
                          <span className="inline-flex items-center gap-2 text-primary-900">
                            {done ? <CheckCircle className="h-4 w-4 text-success-600" /> : <FlaskConical className="h-4 w-4" />}
                            {lab.name}
                            {done ? <span className="text-xs font-semibold text-success-700">Completed</span> : null}
                          </span>
                          <Link
                            to={lab.slug ? `/labs/${lab.slug}?course=${course.slug}` : '/labs'}
                            className={`inline-flex items-center justify-center rounded-md text-xs px-3 py-2 font-medium ${
                              done ? 'border border-gray-300 text-gray-700 hover:bg-gray-50' : 'bg-primary-600 text-white hover:bg-primary-700'
                            }`}
                          >
                            {done ? 'Practice again' : 'Start lab'}
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                </section>
              )}

              {(topic.references || []).length > 0 && (
                <section className="mt-10">
                  <h2 className="text-xl font-semibold text-gray-900 mb-3">Reference links</h2>
                  <ul className="space-y-2">
                    {topic.references!.map((reference, index) => (
                      <li key={reference.id || `${reference.url}-${index}`}>
                        <a
                          href={reference.url}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-2 text-primary-700 font-medium hover:text-primary-900"
                        >
                          <ExternalLink className="h-4 w-4 shrink-0" />
                          {reference.title || reference.url}
                        </a>
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              <TopicNavLinks
                previousSlug={previousSlug}
                previousTitle={previousTitle}
                previousModuleTitle={previousModuleTitle}
                previousHref={previousAssessmentId ? `/courses/${course.slug}/assessments/${previousAssessmentId}` : null}
                nextSlug={nextSlug}
                nextTitle={nextTitle}
                nextModuleTitle={nextModuleTitle}
                nextHref={nextAssessmentId ? `/courses/${course.slug}/assessments/${nextAssessmentId}` : null}
                currentModuleTitle={moduleTitle}
                toTopic={(topicPath) => courseTopicPath(course.slug, topicPath)}
              />

              <div className="mt-10 space-y-3">
                {isLastTopic ? (
                  <div className="rounded-2xl border border-gray-200 p-5">
                    {topicDone || progress?.isCourseCompleted ? (
                      <p className="font-semibold text-gray-900 inline-flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-success-500" /> Completed
                      </p>
                    ) : (
                      <Button type="button" onClick={markTopicDone} disabled={completing}>Done</Button>
                    )}
                  </div>
                ) : (
                  <div className="rounded-2xl border border-gray-200 p-5">
                    <p className="font-semibold text-gray-900 inline-flex items-center gap-2">
                      {topicDone ? <CheckCircle className="h-4 w-4 text-success-500" /> : null}
                      {topicDone ? 'Completed' : 'Topic in progress'}
                    </p>
                    <p className="text-sm text-gray-600 mt-1">
                      {progress?.isCourseCompleted
                        ? 'Completed'
                        : moduleDone
                          ? `${currentProgressModule?.title || 'Module'} completed.`
                          : `${progress?.completedTopicCount || 0}/${progress?.topicCount || 0} topics · ${progress?.completedLabCount || 0}/${progress?.labCount || 0} labs · ${progress?.completedAssessmentCount || 0}/${progress?.assessmentCount || 0} assessments`}
                    </p>
                  </div>
                )}
                {assessmentId && currentProgressModule && (
                  <div className="rounded-2xl border border-primary-200 bg-primary-50 p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div>
                      <p className="font-semibold text-primary-900">
                        {currentProgressModule.assessmentPassed ? 'Module assessment passed' : 'Complete the module assessment'}
                      </p>
                      <p className="text-sm text-primary-800">
                        {assessmentPending
                          ? 'This module is complete only after you pass the assessment.'
                          : 'Review or retake the assessment for this module.'}
                      </p>
                    </div>
                    <Link to={`/courses/${course.slug}/assessments/${assessmentId}`}>
                      <Button size="sm">{currentProgressModule.assessmentPassed ? 'Review assessment' : 'Take assessment'}</Button>
                    </Link>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        <div className="order-3">
          <CourseLearnAside
            courseId={course.id}
            courseSlug={course.slug}
            courseTitle={course.title}
            interviewPackageCount={interviewPackageCount}
            enrollment={enrollment}
            interviewPackages={interviewPackages}
            onEnrollmentChange={(next) => {
              setEnrollment(next);
              setInterviewPackageCount(next.interviewPackageCount || 0);
            }}
            averageRating={readCourseRating(outline || course).average}
            ratingCount={readCourseRating(outline || course).count}
            onRated={(average, count) => {
              setOutline((current) => current ? { ...current, averageRating: average, ratingCount: count } : current);
              setCourse((current) => current ? { ...current, averageRating: average, ratingCount: count } : current);
            }}
          />
        </div>
      </div>
    </article>
  );
};

export default CourseTopicPage;
